import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import useOfferList from 'hooks/Offers/useOfferList'
import LayoutContainer from 'components/Common/LayoutContainer'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import config from 'constants/config'
import {
  OfferListEventsProvider,
} from 'components/OfferList/OfferListEventsContext'
import Group from 'components/utils/Group'
import { EmptyObject } from 'lib/object/objectUtils'
import OfferListCarousel from '../OfferListCarousel'
import HeroOfferCarouselLookingForSomethingElse from 'home/components/LookingForSomethingElse/HeroOfferCarouselLookingForSomethingElse'
import PaletteSheet from 'LuxKit/PaletteSheet'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import getOfferListKey from 'lib/offer/offerListKey'
import { ANYWHERE_NAME, ANYWHERE_PLACE_ID } from 'constants/search'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS } from 'constants/tours'
import SiteTakeoverBackgroundImage from 'takeovers/utils/components/SiteTakeoverBackgroundImage'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Places from 'constants/places'
import TextButton from 'LuxKit/Button/TextButton'
import { take } from 'lib/array/arrayUtils'
import { FormattedMessage } from 'react-intl'
import TourLuxPlusDiscountBanner from 'components/Tours/TourLuxPlusDiscountBanner'

interface Props {
  filters?: App.OfferListFilters
  random?: boolean
  onOffersShow?: (offerIds: Array<string>) => void
}

const SiteTakeoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
`

const Content = styled(Group)`
  position: relative;
  z-index: 1;
`

const ExclusiveOfferContainer = styled(PaletteSheet)`
  position: relative;
  padding: ${rem(64)} 0 ${rem(32)};
  background-color: ${props => props.theme.palette.neutral.default.eight};

  ${mediaQueryUp.tablet} {
    padding: ${rem(96)} 0  ${rem(40)};
  }
`

const TopExclusiveHeaderGrid = styled(PaletteSheet)`
  display: grid;
  align-items: center;
  grid-template:
    "title"
    "link"
    / 1fr max-content;
  grid-gap: ${rem(12)};
  grid-column-gap: 0;

  ${mediaQueryUp.tablet} {
    grid-template:
      "title link"
      / 1fr max-content;
    grid-gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;
  }

  > .link-area {
    grid-area: link;
  }
`

function TourHeroOfferCarousel(props: Props) {
  const {
    filters = EmptyObject,
    random,
    onOffersShow,
  } = props

  const currentSiteTakeover = useCurrentSiteTakeover({ heroVertical: 'tours' })
  const [selectedFilter, setSelectedFilter] = useState<string | undefined>()

  const filtersOnLocation = useMemo(() => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return {
        ...filters,
        destinations: selectedFilter ? [selectedFilter] : TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
      }
    }
    return filters
  }, [filters, currentSiteTakeover, selectedFilter])

  const tourSearchURls = useMemo(() => {
    let urlSearchParams
    if (currentSiteTakeover?.id === 'tourbestasia') {
      const { destinations, operators } = filtersOnLocation
      urlSearchParams = new URLSearchParams({
        destinationId: Places.Asia.id,
        destinationName: Places.Asia.name,
        destinations,
        operators,
      })
    } else {
      urlSearchParams = new URLSearchParams({
        destinationId: ANYWHERE_PLACE_ID,
        destinationName: ANYWHERE_NAME,
        categories: 'luxexclusive',
      })
    }
    return `/search/tours?${urlSearchParams.toString()}`
  }, [filtersOnLocation, currentSiteTakeover])

  const filterOptions = useMemo<Array<{ label: string, value: string }>>(() => {
    if (currentSiteTakeover?.id === 'tourbestasia') {
      return [
        { label: Places.Vietnam.name, value: Places.Vietnam.id },
        { label: Places.Japan.name, value: Places.Japan.id },
        { label: Places.SriLanka.name, value: Places.SriLanka.id },
      ]
    }
    return []
  }, [currentSiteTakeover])

  const toggleFilter = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const filter = e.currentTarget.dataset.value!
    const nextFilter = selectedFilter === filter ? undefined : filter
    setSelectedFilter(nextFilter)
  }, [selectedFilter])

  const offerList = useOfferList(filtersOnLocation, { randomize: random })
  const tracking = useOfferListTracking(getListName('OfferList', 'TOURS', 'Hero Carousel'), { key: getOfferListKey(filters) })

  const hasInitOffersLoaded = useRef(false)
  useEffect(() => {
    if (!hasInitOffersLoaded.current && !offerList.fetching && !!offerList.offerIds.length) {
      hasInitOffersLoaded.current = true
      onOffersShow?.(take(offerList.offerIds, 3))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offerList])

  const paletteType = currentSiteTakeover?.palette ?? 'inverse'
  const headerPaletteType = currentSiteTakeover?.heroSection?.palette ?? 'inverse'

  return (
    <ExclusiveOfferContainer paletteType={paletteType}>
      {currentSiteTakeover && <SiteTakeoverBackground>
        <SiteTakeoverBackgroundImage background="hero" vertical="tours" />
      </SiteTakeoverBackground>}
      <Content direction="vertical" gap={24} tabletGap={32}>
        <LayoutContainer>
          {config.TOUR_LUXPLUS_CAMPAIGN_ENABLED && <TourLuxPlusDiscountBanner />}
          <TopExclusiveHeaderGrid paletteType={headerPaletteType}>
            <Group className="title-area" direction="horizontal" gap={12}>
              {currentSiteTakeover?.heroSection?.logo}
              <Group direction="vertical" verticalAlign="center">
                {!currentSiteTakeover && <Heading className="title-area" variant="heading2" align="start">
                  <FormattedMessage
                    id="l3wbcUEHDJMUgG_szQyX"
                    defaultMessage="Today’s top <i>exclusive</i> offers"
                    description="Top picks for me carousel title with italic 'exclusive'"
                    values={{
                      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                    }}
                  />
                </Heading>}
                {currentSiteTakeover?.heroSection?.title}
                {currentSiteTakeover?.heroSection?.subtitle}
              </Group>
            </Group>
            <Group className="link-area" direction="horizontal" gap={16} verticalAlign="center">
              <PaletteSheet paletteType="default">
                {!!filterOptions.length && <Group direction="horizontal" gap={8}>
                  {filterOptions.map(option =>
                    <FilterChip
                      key={option.value}
                      size="medium"
                      selected={option.value === selectedFilter}
                      data-value={option.value}
                      onClick={toggleFilter}
                    >
                      {option.label}
                    </FilterChip>,
                  )}
                </Group>}
              </PaletteSheet>
              <TextButton kind="tertiary" to={tourSearchURls}>
                <FormattedMessage
                  id="qywu0sy_1NVQHCWxPYIf"
                  defaultMessage="View all"
                  description="Button text to view all available items in the current category or listing section"
                />
              </TextButton>
            </Group>
          </TopExclusiveHeaderGrid>
        </LayoutContainer>
        <OfferListEventsProvider tracking={tracking}>
          <OfferListCarousel
            tileStyle="card-default"
            tileSize="medium"
            overrideOfferList={offerList}
            limit={15}
          >
            <HeroOfferCarouselLookingForSomethingElse
              link={tourSearchURls}
              offerType="tours"
            />
          </OfferListCarousel>
        </OfferListEventsProvider>
      </Content>
    </ExclusiveOfferContainer>
  )
}

export default TourHeroOfferCarousel
